import { ZoomOutIcon } from "lucide-react";
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { NativePdfPortal } from "src/components/NativePdf/NativePdfPortal";
import Skeleton from "src/components/Skeleton";
import Tooltip from "src/components/Tooltip";
import Touchable from "src/components/Touchable";
import { Button } from "src/components/ui/button";
import { useDimensions, useVirtualizedPdfPages } from "src/hooks";
import { DEFAULT_PDF_PAGE_RATIO } from "src/hooks/useVirtualizedPdfPages";
import {
  canOpenNativePdfSource,
  isNativePdfAvailable,
  type NativePdfSource,
  useNativePdfDocument,
} from "src/lib/runtime/nativePdf";

import { cn } from "src/utilities/shadcnUtils";
import useKeyboardListener from "../../hooks/useKeyboardListener";

const HEADERS = { httpHeaders: { Accept: "application/pdf" } };

type BinaryData = ArrayBuffer | Uint8Array | string;
type Source = { data: BinaryData | undefined } | { range: any } | { url: string };
type File = string | ArrayBuffer | Blob | Source | null;

export interface ScrollablePDFViewerProps {
  file: File;
  className?: string;
  newTab?: boolean;
  zoom?: boolean;
  children?: ReactNode;
  rotate?: number;
  renderTextLayer?: boolean;
  renderAnnotationLayer?: boolean;
  documentClassName?: string;
  loading?: boolean;
  onLoadError?: (error: Error) => void;
  /** 1-indexed page to scroll into view once the document loads. */
  initialPage?: number;
  /**
   * Tokens to highlight on `initialPage` (or any page if `initialPage` is
   * unset). The viewer extracts text positions from PDF.js, finds the
   * densest spatial cluster of matching items, and wraps that contiguous
   * block in a `<mark>` via `customTextRenderer`. Requires
   * `renderTextLayer` to be true.
   */
  highlightText?: string;
}

const STYLES = {
  container: "relative flex flex-col items-end",
  docWrapper:
    "h-full w-full flex flex-col bg-white border border-solid border-gray-400 [overflow:overlay] overflow-x-scroll [&::-webkit-scrollbar]:appearance-none [&::-webkit-scrollbar]:w-[7px] [&::-webkit-scrollbar]:h-[7px] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-black/50 [&::-webkit-scrollbar-thumb]:shadow-[0_0_1px_rgba(255,255,255,0.5)]",
  zoomDocWrapper: "hover:cursor-zoom-in",
  zoomOutDocWrapper: "hover:cursor-zoom-out",
  button: "mt-1 text-green-500",
  zoomResetButton: "absolute top-[10px] left-[10px]",
};

const ScrollablePDFViewer = ({
  file,
  className,
  newTab = true,
  renderTextLayer = false,
  renderAnnotationLayer = false,
  zoom,
  children,
  rotate,
  documentClassName,
  loading: loadingProp,
  onLoadError,
  initialPage,
  highlightText,
}: ScrollablePDFViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const docWrapperRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToInitialPage = useRef(false);
  // Captured PDFDocumentProxy from onLoadSuccess. Needed to call
  // getTextContent() for the cited page so we can compute spatial highlight
  // clusters (see effect below).
  const [pdfDocument, setPdfDocument] = useState<{
    getPage: (n: number) => Promise<{
      getTextContent: () => Promise<{
        items: Array<{ str: string; transform: number[] }>;
      }>;
    }>;
  } | null>(null);
  // Per-page set of itemIndexes to wrap in <mark>. Computed from the
  // density-cluster algorithm once the doc + initialPage are known.
  const [highlightItemIndexes, setHighlightItemIndexes] = useState<Map<number, Set<number>>>(() => new Map());
  const nativePdfSource = useMemo((): NativePdfSource | undefined => {
    if (!file) {
      return undefined;
    }
    if (typeof file === "string") {
      return { type: "url", url: file };
    }
    if (file instanceof Blob) {
      const fileName = file instanceof globalThis.File ? file.name : "blob";
      const lastModified = file instanceof globalThis.File ? file.lastModified : 0;
      return {
        type: "blob",
        blob: file,
        sourceId: `scrollable-pdf:${fileName}:${file.size}:${lastModified}`,
      };
    }
    if (typeof file === "object" && "url" in file) {
      return { type: "url", url: file.url };
    }
    if (typeof file === "object" && "data" in file && file.data instanceof ArrayBuffer) {
      return {
        type: "bytes",
        bytes: file.data,
        sourceId: `scrollable-pdf:array-buffer:${file.data.byteLength}`,
      };
    }
    if (typeof file === "object" && "data" in file && file.data instanceof Uint8Array) {
      return {
        type: "bytes",
        bytes: file.data,
        sourceId: `scrollable-pdf:uint8-array:${file.data.byteLength}`,
      };
    }
    return undefined;
  }, [file]);
  const nativePdfAvailable = isNativePdfAvailable();
  const nativePdfPlainVisual = !renderTextLayer && !renderAnnotationLayer && !highlightText;
  const nativePdfDocument = useNativePdfDocument({
    source: nativePdfSource,
    enabled:
      nativePdfAvailable && nativePdfPlainVisual && canOpenNativePdfSource(nativePdfSource) && !loadingProp,
  });
  const useNativePdfRenderer =
    nativePdfAvailable &&
    nativePdfPlainVisual &&
    canOpenNativePdfSource(nativePdfSource) &&
    !nativePdfDocument.isError;

  // react-pdf's typed OnDocumentLoadSuccess passes a PDFDocumentProxy. We
  // use a structural subset (numPages + getPage) instead of pulling in the
  // full pdfjs-dist types — `any` here keeps the callback assignable to
  // react-pdf's signature without losing what we actually use.
  const onDocumentLoadSuccess = useCallback((doc: any) => {
    setNumPages(doc.numPages);
    // The same arg is the PDFDocumentProxy with getPage on it. Stash it so
    // the highlight effect can pull text positions for the cited page.
    if (typeof doc.getPage === "function") {
      setPdfDocument(doc);
    }
  }, []);

  const { width, height: containerHeight, observe } = useDimensions<HTMLDivElement>();

  // Virtualized page window: only the pages near the viewport are mounted, so
  // large PDFs no longer rasterize every page up front. The estimate uses the
  // portrait aspect ratio; `measureElement` corrects each page to its real
  // height once rendered.
  const estimateSize = useCallback(
    () => Math.round((width || 600) * scale * DEFAULT_PDF_PAGE_RATIO),
    [width, scale]
  );

  const { virtualizer, virtualItems, totalSize, scrollToPage } = useVirtualizedPdfPages({
    count: numPages ?? 0,
    scrollElementRef: docWrapperRef,
    estimateSize,
    overscan: 2,
  });

  // Significant tokens (≥5 chars) from the highlight anchor. Filtering short
  // words avoids highlighting the/for/and/etc. on every page.
  const needleWords = useMemo(() => {
    if (!highlightText) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of highlightText.toLowerCase().split(/[^a-z0-9]+/)) {
      if (raw.length < 5) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      out.push(raw);
    }
    return out;
  }, [highlightText]);

  // Compute the densest cluster of matching text items on the cited page,
  // then mark a contiguous itemIndex range covering it. Stray matches
  // outside the cluster get discarded.
  useEffect(() => {
    if (!pdfDocument || !initialPage || needleWords.length === 0) {
      setHighlightItemIndexes(new Map());
      return;
    }
    let cancelled = false;
    pdfDocument.getPage(initialPage).then(async (page) => {
      const textContent = await page.getTextContent();
      if (cancelled) return;
      const matches: Array<{ index: number; y: number }> = [];
      textContent.items.forEach((item, i) => {
        if (!item.str) return;
        const lower = item.str.toLowerCase();
        if (needleWords.some((w) => lower.includes(w))) {
          // transform = [a, b, c, d, x, y]; index 5 is the page-relative y
          // origin (from bottom in PDF coords).
          matches.push({ index: i, y: item.transform[5] ?? 0 });
        }
      });
      if (matches.length === 0) {
        setHighlightItemIndexes(new Map());
        return;
      }
      // Densest cluster: for each candidate, count neighbors within ±50
      // PDF units (~3-4 lines). Pick the candidate with the most neighbors.
      const PROXIMITY_Y = 50;
      let bestCenter = matches[0];
      let bestCount = -1;
      for (const m of matches) {
        const count = matches.filter((o) => Math.abs(o.y - m.y) <= PROXIMITY_Y).length;
        if (count > bestCount) {
          bestCount = count;
          bestCenter = m;
        }
      }
      const cluster = matches.filter((m) => Math.abs(m.y - bestCenter.y) <= PROXIMITY_Y);
      const minIdx = Math.min(...cluster.map((m) => m.index));
      // Cap the contiguous range so a single false match far from the cluster
      // can't blow it out across the whole page.
      const MAX_RANGE = 30;
      const maxIdx = Math.min(Math.max(...cluster.map((m) => m.index)), minIdx + MAX_RANGE);
      const set = new Set<number>();
      for (let i = minIdx; i <= maxIdx; i += 1) set.add(i);
      setHighlightItemIndexes(new Map([[initialPage, set]]));
    });
    return () => {
      cancelled = true;
    };
  }, [pdfDocument, initialPage, needleWords]);

  const customTextRenderer = useMemo(() => {
    if (highlightItemIndexes.size === 0) return undefined;
    return (textItem: { str: string; pageNumber?: number; itemIndex?: number }) => {
      const str = textItem.str;
      const escaped = str.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);
      const pageSet = textItem.pageNumber != null ? highlightItemIndexes.get(textItem.pageNumber) : undefined;
      if (!pageSet || textItem.itemIndex == null || !pageSet.has(textItem.itemIndex)) return escaped;
      return `<mark>${escaped}</mark>`;
    };
  }, [highlightItemIndexes]);

  // Scroll the cited page into view once the document's page count is known.
  // The virtualizer computes offsets from estimates (corrected by live
  // measurement), so we no longer need to wait for every preceding page to
  // rasterize — `scrollToPage` brings the target into the window and the
  // offset self-corrects as pages render.
  useEffect(() => {
    if (!initialPage || !numPages || hasScrolledToInitialPage.current) return;
    if (initialPage < 1 || initialPage > numPages) return;
    scrollToPage(initialPage);
    hasScrolledToInitialPage.current = true;
  }, [initialPage, numPages, scrollToPage]);

  // Reset scroll flag, captured doc and highlight map whenever the file source
  // changes — opening a new PDF in the same viewer instance should start clean.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    hasScrolledToInitialPage.current = false;
    setPdfDocument(null);
    setHighlightItemIndexes(new Map());
  }, [file]);

  const handleResetZoom = useCallback(() => {
    setScale(1);
  }, []);

  const loadingSkeleton = (
    <div style={{ height: containerHeight || 600, width }}>
      <Skeleton className="w-full h-full rounded-0" />
    </div>
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!zoom) {
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        // zoom out
        setScale((prev) => prev - 0.2);
      } else {
        // zoom in
        setScale((prev) => prev + 0.2);
      }
    },
    [zoom]
  );

  const handleZoomOut = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!zoom) {
        return;
      }
      event?.preventDefault();
      // zoom out
      setScale((prev) => prev - 0.2);
    },
    [zoom]
  );

  const holdingDownCmd = useKeyboardListener(["Meta", "Control"]);

  return (
    <div ref={observe} className={cn("max-h-full relative overflow-hidden", STYLES.container, className)}>
      <div
        ref={docWrapperRef}
        className={cn(
          "rounded lg:overscroll-contain",
          STYLES.docWrapper,
          zoom && STYLES.zoomDocWrapper,
          zoom && holdingDownCmd && STYLES.zoomOutDocWrapper,
          documentClassName
        )}
        onClick={zoom ? handleClick : undefined}
        onContextMenu={zoom ? handleZoomOut : undefined}
      >
        {loadingProp ? (
          loadingSkeleton
        ) : useNativePdfRenderer ? (
          nativePdfDocument.data ? (
            <NativePdfPortal
              documentId={nativePdfDocument.data.documentId}
              scale={scale}
              rotation={rotate}
              onScaleChange={setScale}
              onError={onLoadError}
              className="h-full w-full"
            />
          ) : (
            loadingSkeleton
          )
        ) : (
          <Document
            options={HEADERS}
            rotate={rotate}
            file={file as any}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onLoadError}
            onSourceError={onLoadError}
            loading={loadingSkeleton}
          >
            {/* Virtualized: spacer holds the full scroll height; only the windowed
                pages are mounted, each absolutely positioned at its measured offset. */}
            <div className="shrink-0" style={{ position: "relative", height: totalSize, width: "100%" }}>
              {virtualItems.map((vi) => {
                const pageNum = vi.index + 1;
                return (
                  <div
                    key={vi.key}
                    data-index={vi.index}
                    data-pdf-page={pageNum}
                    ref={virtualizer.measureElement}
                    className="relative"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${vi.start}px)`,
                    }}
                  >
                    <Page
                      rotate={rotate}
                      pageNumber={pageNum}
                      loading={loadingSkeleton}
                      width={width * scale}
                      renderTextLayer={!zoom && renderTextLayer}
                      renderAnnotationLayer={renderAnnotationLayer}
                      customTextRenderer={customTextRenderer}
                    />
                    <div className="absolute bottom-2 right-2 w-fit bg-black/60 text-primary-foreground text-xs px-2 py-1 rounded pointer-events-none z-10">
                      {pageNum} / {numPages}
                    </div>
                  </div>
                );
              })}
            </div>
          </Document>
        )}
      </div>
      {newTab && (
        <a
          href={
            typeof file === "string"
              ? file
              : file && typeof file === "object" && "url" in file
                ? file?.url
                : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
        >
          <Button variant="ghost" className={cn(STYLES.button, "w-fit max-[420px]:w-full justify-end")}>
            View in new tab &#8594;
          </Button>
        </a>
      )}
      {zoom && scale !== 1 && (
        <Tooltip white text="Reset zoom">
          <Touchable className={STYLES.zoomResetButton} onClick={handleResetZoom}>
            <ZoomOutIcon className="text-gray" />
          </Touchable>
        </Tooltip>
      )}
      {children}
    </div>
  );
};

export default memo(ScrollablePDFViewer);
